/**
 * @description 封装端能力，便于使用
 */
import boxx from '@baidu/boxx-fe';
import {
    DEFAULT_USER_AVATAR,
    ERRNO_LIST_AUTH,
    H5_SEND_FIRST_FRAME_ALREADY_STATUS,
    MATRI_STYLE,
    MODELMAP,
    NA_FEEDBACK_CLOSE,
    PAGE_TYPE,
    RISK_CONTROL_TIPKEY_MAP,
    SEEK_TAB_PARAMS,
    SHARE_PANEL_ITEM,
    TTS_STATE_CONFIG,
} from '@lib/constants';
import {sendYaLog} from '@lib/log';
import {sendMessage} from '@lib/utils/messageCenter';
import {invokeSchema} from '@lib/utils/schema';
import {merge} from 'lodash-es';
import __INSTANCE__ from '../InstanceManager';
import {isPcClient} from './env';
import {THEME_MODE} from './pageMode';
import {
    appVersionCompare,
    copyToClipBoard,
    inNewApp,
    isHarmonyOS,
    isIOS,
} from './tools';

const {isAndroid} = boxx.env;
type NetworkType = 'wifi' | '3G' | '4g' | '5g' | 'none' | 'unknown';

// 每次进入页面个性语音包仅提示一次标记
let isShowUgcTip = false;
// 每次进入页面，只发一次首帧消息
let isSendFirstFrame = false;

interface BaseResponse {
    status: string | number;
    message: string;
}

interface FeToNaData {
    action: string;
    params?: {
        [key: string]: string;
    };
}

interface NetworkStatusData {
    networkType: NetworkType;
    isWeakNet?: '1' | '0';
}

export interface SystemThemeData {
    data: {
        // 皮肤类别
        skin?: any;
        // 主题类别， 取值范围：light、dark
        theme: THEME_MODE;
    };
}

interface NetworkStatus extends BaseResponse {
    /**
     * 返回的数据
     */
    data: {
        /**
         * 网络类型 wifi ｜ 4G ｜ none .....
         */
        networkType: NetworkType;
        isWeakNet?: '1' | '0';
    };
}

interface MusicPlayerParams {
    action: 'play' | 'pause' | 'show' | 'init';
    url?: string;
    showFloating?: 0 | 1;
    messageId: string;
    cover?: string;
    needPay?: 0 | 1;
    musicId?: string;
    sitePinyin?: string;
    singer?: string;
    name?: string;
}

interface AgreementCancelDialogParams {
    cancelButton?: string;
    msg?: string;
    title?: string;
    okButton?: string;
}

interface StorageRes extends BaseResponse {
    /**
     * 返回的数据
     */
    data: string;
}

interface ResOfCommonParams extends BaseResponse {
    data: {
        /**
         * 安卓会在url上拼好参数
         */
        url: string;
        /**
         * ios只返回公参 appname=newapp&cfrom=1099a&ds_lv=7..........
         */
        params: string;
    };
}

interface ResOfGetVolume extends BaseResponse {
    data: {
        /**
         * 音量大小 0 - 1 保留两位小数
         */
        volume: string;
    };
}

interface StatusBarHeightRes extends BaseResponse {
    /**
     * 返回的数据
     */
    data: {
        /**
         *
        */
        height: number;
    };
}

interface BarIconInfoRes extends BaseResponse {
    /**
     * 返回的数据
     */
    data: iconInfo;
}

interface iconInfo {
    iconWidth?: number;
    iconLeft?: number;
    iconRight?: number;
    barHeight?: number;
    isShowSearch?: boolean;
}
interface AuthenticationRes extends BaseResponse {
    /**
     * 返回的数据
     */
    data: {
        /**
         * 水印字符
        */
        authentication: string;
    };
}

interface ResOfGetFontSize extends BaseResponse {
    data: {
        /**
         * 1-小 2-标准 3-大 4-特大
         */
        fontSize: string;
    };
}
interface ResOfGetSettingInfo extends BaseResponse {
    data: {
        /**
         * tts开关状态 0-关 1-开
         */
        tts: string;
    };
}

interface ResOfGetGlobalSettings extends BaseResponse {
    data: {
        speakSpeed?: string;
        speakerId?: string;
        fontSize?: string;
        aiId?: string;
        robots?: string;
        theme?: string;
        skin?: string;
        individual?: string;
        assistantImageStatus?: '0' | '1';
        matrixstyle?: MATRI_STYLE;
    };
}

interface ResOfSetGlobalSettings extends BaseResponse {
    data: {};
}

interface ResOfGetUserInfo extends BaseResponse {
    data: UserInfo;
}

interface UserInfo {
    avatar?: string;
    modelStatus?: string;
    nickName?: string;
    uk?: string;
}

interface ResModelStatus extends BaseResponse {
    data: ModelStatus;
}

interface ModelStatus {
    status?: string;
}

interface DigitVoiceData {
    mid?: string;
    md5?: string;
    ttsStatus?: number;
    cmd?: string;
    ttsStatusV2?: number;
}

interface SendPageTrack {
    action: string;
    data: {};
}

interface ShareDialog {
    linkText?: string;
    link?: string;
    cover?: string;
    brief?: string;
    title?: string;
    source: string;
    shareType: string;
    ubcExt?: { [key: string]: string };
    image?: { [key: string]: string };
    dialogTitle?: string;
    platforms?: string[];
    publisher?: {
        video?: {
            coverUrl: string;
            videoUrl: string;
            videoFrom: string;
        };
    };
    textContent?: string;
}

interface ShowMenu {
    source: string;
    title: string;
    brief: string;
    cover: string;
    link: string;
    copyShare: string;
    isHost?: string;
    deleteNid?: string;
}

interface OpenChatParams {
    botId?: string;
    source?: string;
    title?: string;
    plugin?: object;
    inputText?: string;
    inputImageUrl?: string;
    characterId?: string;
    isOffice?: number;
    voiceMode?: object;
    voiceChatMode?: object;
}

interface OpenPromptParams {
    promptId?: string;
    promptName?: string;
    tips?: string;
    actionId?: string;
    render?: string;
    renderOperate?: object;
    pluginId?: string;
    inputText?: string;
    inputImageUrl?: string;
    isQuery?: string;
    plugin?: object;
}

interface CommunityInfo {
    promptId?: string;
    characterId?: string;
    topicId?: string;
    nid?: string;
    chatInfo?: {
        schema: string;
        name?: string;
        pic?: string;
        isDigital?: number;
        chatNum: number;
    };
    like?: {
        islike: number;
        likeNum: number;
    };
    aiCommentInput?: object;
    errno?: number;
    user?: {
        uk: string;
        nickName: string;
        avatar: string;
    };
}

interface SendMessageData {
    action: string;
    params: {};
}

interface GetHostTagResponse {
    data: {
        tag: 'main_chat' | 'second_chat' | 'unknown';
    };
}

interface OpenCharacterSettingsParams {
    characterId: string;
    characterName: string;
}

interface OpenDetailParams {
    type: string;
    url: string;
    d_args?: {
        [key: string]: string;
    };
    frameExt?: {
        [key: string]: string | object[];
    };
    /* 允许系统长按 */
    allowSystemLongPress?: 1 | 0;
    /* 控制手势 */
    fullScreenSlideClose?: 1 | 0;
    /* 是否禁用中间右滑返回 0-不禁用；1-禁用 */
    forceForbiddenSlide?: 0 | 1;
    leftTopIcon?: string;
    topBarConfig?: {
        layout: string;
        title: string;
        titleLightColor?: string;
        titleDarkColor?: string;
        bgLightColor?: string;
        bgDarkColor?: string;
    };
    enableCloseBtn?: 0 | 1;
    /* 是否使用暗黑样式loading */
    isDarkMode?: '0' | '1';
    isAlertView?: 0 | 1;
    isLandscape?: string;
    extra?: {
        [key: string]: any;
    };
    canOpenPage?: 0 | 1;
    feCustomLongPress?: number;
    openAnimation?: number;
}

interface HideLoadingParams {
    sessionId?: string;
    isSuccess?: string;
}

export interface ImageUrls {
    // 图片地址
    src: string;
    // 是否展示AI修图
    showRepairImage?: boolean;
    // 是否展示图片对话
    showImageChat?: boolean;
    // 图片原网页
    originWebUrl?: string;
    // 图片原网页标题
    originWebTitle?: string;
}

interface ImgPreviewParams {
    urls: string[];
    current?: string;
    logParams?: {
        params?: unknown;
        ext?: unknown;
    };
    waterMarkUrl?: string;
    currentIndex: number;
    imageUrls?: ImageUrls[];
    pcLogParams?: {
        params?: unknown;
        ext?: unknown;
    };
}

interface RiskControlParams {
    sessionId?: string;
    code?: number;
    message?: string;
    tipKey?: string;
    botId?: string;
    chatState?: {
        isSafe?: number;
        isHistory?: boolean;
    };
    modelData?: ModelData;
    title?: string;
    buttonTitle?: string;
    buttonType?: string;
    toastMsg?: string;
    chatDisable?: 1 | 0;
    displayType?: 1 | 0;
    ext?: {
        [key: string]: string | Record<string, any>;
    };
}

interface ModelData {
    /**
     * 点击要切换的模型id
     */
    switchModelId?: string;
    /**
     * 可用时间
     */
    availableTime?: number;
    /**
     * 文案
     */
    tips?: {
        /**
        * 第一行文案
        */
        title: string;
        /**
        * 第二行文案
        */
        content: string;
        /**
        * 发送时toast文案
        */
        action?: string;
    };
}

interface GetWechatInstalledRsp extends BaseResponse {
    data: {
        /**
         * 0: 未安装，1：已安装
         */
        installed: number;
    };
}

interface ModelsInfoRes extends BaseResponse {
    /**
     * 返回的数据
     */
    data: ModelsInfo;
}

interface ModelsInfo {
    webSearch?: boolean;
    deepSeek?: boolean;
    select?: string;
    force?: string;
}

// pc客户端的canIUse能力
const pcClientCanIUse = (key: string) => {
    return isPcClient() && window?.native?.canIUse && window.native.canIUse(key);
};

interface CloseBrowseParams {
    forbiddenAnimation?: 0 | 1;
}
/**
 * @description 关闭当前的新浏览框架
 */
const closeBrowse = (params?: CloseBrowseParams | object) => {
    if (boxx.canIUse('newbrowse.close')) {
        const forbiddenAnimation
            = (params
                && 'forbiddenAnimation' in params
                && params.forbiddenAnimation)
            || 0;
        boxx.newbrowse.close({
            forbiddenAnimation,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('newbrowse.close 端能力不存在！！！');
    }
};

/**
 * @description 使用webStroage 存储数据
 * @param action 操作方式 'set' | 'get' | 'delete'
 * @param key 存储时对应的key
 * @param data key对应的value
 */
const useWebStorage = (action: 'set' | 'get' | 'delete', key: string, data?: string): Promise<StorageRes> => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.utils.webStorage')) {
            boxx.newapp.utils.webStorage({
                action,
                key,
                data,
                success: (res: StorageRes) => {
                    if (isAndroid || isHarmonyOS) {
                        // 安卓返回的data是个对象，ios是字符串
                        res.data = (res.data as any)?.value || {};
                    }
                    resolve(res);
                },
                fail: () => {
                    resolve({
                        status: 'fail',
                        message: '端能力调用失败！！！',
                        data: '',
                    });
                },
            });
        }
        else if (pcClientCanIUse('native:utils-webStorage')) {
            window.native.invoke('native:utils-webStorage', {
                action,
                key,
                data: data || '',
            }).then((res: any) => {
                resolve(res);
            }).catch(() => {
                resolve({
                    status: 'fail',
                    message: '端能力调用失败！！！',
                    data: '',
                });
            });
        }
        else if (typeof localStorage === 'object') {
            let value = '';
            let status = 'success';
            let message = '成功';
            try {
                switch (action) {
                    case 'set':
                        localStorage.setItem(key, data || '');
                        break;
                    case 'get':
                        value = localStorage.getItem(key) || '';
                        break;
                    case 'delete':
                        localStorage.removeItem(key);
                        break;
                }
            }
            catch (e) {
                status = 'fail';
                message = 'localStorage 调用失败';
            }
            resolve({
                status,
                message,
                data: value,
            });
        }
        else {
            resolve({
                status: 'fail',
                message: '端能力不存在！！！',
                data: '',
            });
        }
    });
};

const showNaToast = (text: string, time = '3', overFloatWindow = '0') => {
    // 评论半屏页面时，toast在需要在浮窗上显示安卓需要overFloatWindow传1
    overFloatWindow = window.globalData?.hostTag === PAGE_TYPE.COMMENT ? '1' : overFloatWindow;
    if (boxx.canIUse('commonUI.showToast')) {
        boxx.commonUI.showToast({
            type: '1',
            message: text,
            time,
            overFloatWindow,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('commonUI.showToast 端能力不存在！！！');
        if (!inNewApp || isPcClient()) {
            // lui 的__INSTANCE__ 和chat 的__INSTANCE__ 不是一个实例，lui获取不到PC传入的实例，这里做兼容
            let $root = __INSTANCE__.config.$scroller.parentElement as HTMLElement;
            let isBackup = false;
            if (!$root) {
                isBackup = true;
                $root = document.getElementById('app') || document.getElementById('root') || document.documentElement;
            }

            const id = 'TOAST_CONTENT';
            document.getElementById(id)?.remove();
            const $text = document.createTextNode(text);
            const $toast = document.createElement('div');
            $toast.id = id;

            $toast.style.cssText = `
                position: ${window.liteChatInfo ? 'fixed' : 'absolute'};
                z-index: 1000;
                bottom: 80px;
                left: 50%;
                transform: translateX(-50%);
                background: #000;
                color: #fff;
                font-size: var(--font16_5);
                line-height: 1.2;
                border-radius: 12px;
                padding: 12px 18px;
                text-align: center;
            `;

            $toast.appendChild($text);
            if (isBackup) {
                $toast.style.bottom = '30%';
            }
            $root && $root.appendChild($toast);

            setTimeout(() => {
                $toast.remove();
            }, 3000);
        }
    }
};

/**
 * @description 调起图片查看器
 * @description https://ku.baidu-int.com/knowledge/HFVrC7hq1Q/pKzJfZczuc/2bDtTCu3yf/YT39t8XlP59O5E
 */
const invokeImgPreview = (params: ImgPreviewParams) => {
    if (!params) {
        return;
    }
    if (boxx.canIUse('album.image.preview')) {
        // 移动端不需要pcLogParams
        const {pcLogParams, ...rest} = params;
        boxx.album.image.preview({
            ...rest,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else if (pcClientCanIUse('native:image-preview')) {
        window.native.send('native:image-preview', params);
    }
    else {
        console.error('album.image.preview 端能力不存在！！！');
    }
};

/**
 * @description 跳转或打开图生视频主页面（弹窗），imagelist.length>1是弹窗
 * @description https://es.baidu-int.com/index#/apidetail/2893/invoke
 */
const openImageToVideoMainPage = (params: {
    dialogBtnText?: string;
    dialogTitle?: string;
    templateId?: string;
    title?: string;
    removeWatermark?: number;
    riskTips?: {
        img: string;
        text: string;
    };
    from?: string;
    desc?: string;
    images?: Array<{
        height?: number;
        width?: number;
        id?: number;
        url?: string;
    }>;
}) => {
    if (!params) {
        return;
    }
    if (boxx.canIUse('works.openImageToVideoMainPage') && appVersionCompare('4.12') >= 0) {
        // 移动端不需要pcLogParams
        boxx.works.openImageToVideoMainPage({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('works.openImageToVideoMainPage 端能力不存在！！！');
    }
};

/**
 * @description 跳转tab页面
 * @param tab 跳转的tab参数
 * @param successTip 跳转成功后的提示语
 */
const seekTab = (tab: SEEK_TAB_PARAMS, successTip?: string) => {
    if (boxx.canIUse('newapp.newsystem.seekTab')) {
        boxx.newapp.newsystem.seekTab({
            tab,
            'success_tip': successTip,
        });
    }
    else {
        console.error('newapp.newsystem.seekTab 端能力不存在！！！');
    }
};

/**
 * @description 获取字体大小 http://es.baidu-int.com/index#/apidetail/2286
 * @return Promise<string> 字体大小
 */
const getFontSize = (): Promise<string> => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.system.getGlobalFontSizeSettings')) {
            boxx.newapp.system.getGlobalFontSizeSettings({
                success: (res: ResOfGetFontSize) => {
                    resolve(res.data.fontSize);
                },
                fail: () => {
                    resolve('2');
                },
            });
        }
        else {
            resolve('2');
        }
    });
};

/**
 * @description 获取网络状态
 * @returns resolve NetworkStatusData
 * getNetworkStatus().then(res => {}) 返回的是一个Promise对象
 */
const getNetworkStatus = (): Promise<NetworkStatusData> => {
    return new Promise(resolve => {
        if (boxx.canIUse('device.getNetworkType')) {
            boxx.device.getNetworkType({
                success: (res: NetworkStatus) => {
                    resolve(res.data);
                },
                fail: () => {
                    resolve({
                        networkType: 'none',
                    });
                },
            });
        }
        else {
            if (window.navigator.onLine) {
                resolve({
                    networkType: navigator?.connection?.effectiveType,
                });
            }
            else {
                resolve({
                    networkType: 'none',
                });
            }
        }
    });
};

/**
 * @description 设备短震
 * @description http://es.baidu-int.com/#/apidetail/51/params
 */
const deviceVibrate = (type = 0) => {
    if (boxx.canIUse('device.vibrate')) {
        boxx.device.vibrate({
            type,
            vibrationEffect: {
                timings: [30],
                amplitudes: [-1],
            },
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('device.vibrate 端能力不存在！！！');
    }
};

// 默认兜底，防止首次回调报错
window.audioStatusChangeStopCallback = () => { };
/**
 * @description H5音频播报焦点监听 http://es.baidu-int.com/index#/apidetail/2261/params
 * @param stopCallback 停止回调函数
 * @param webviewId webviewId
 * @param type 'start' | 'end'
 */
const audioStatusChange = (webviewId: string, type: 'start' | 'end', stopCallback: () => void) => {
    if (boxx.canIUse('newapp.newaudio.audioStatusChange') && inNewApp) {
        window.audioStatusChangeStopCallback = stopCallback;
        boxx.newapp.newaudio.audioStatusChange({
            stopCallback: 'audioStatusChangeStopCallback',
            webviewId,
            type,
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('newapp.newaudio.audioStatusChange 端能力不存在！！！');
    }
};

/**
 * @description 隐藏loading
 */
const hideLoading = (params: HideLoadingParams = {}) => {
    /**
     * H5通知NA发送首帧已经准备好，目前的实现比较模糊，在hideLoading: success的时候触发
     */
    const sendFirstFrameReady = () => {
        sendYaLog(`fe_sendFirstFrameReady_webViewID=${window.webviewId}_sessionID=${params.sessionId}_message`);
        sendMessage(H5_SEND_FIRST_FRAME_ALREADY_STATUS, {});
        isSendFirstFrame = true;
    };
    params?.isSuccess === '1' && !isSendFirstFrame && sendFirstFrameReady();

    if (boxx.canIUse('newapp.utils.newHideLoading')) {
        boxx.newapp.utils.newHideLoading({
            webviewId: window.webviewId || '',
            ...params,
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
        sendYaLog(`fe_hideloading_webViewID=${window.webviewId}_sessionID=${params.sessionId}_message`);
    }
    else {
        console.error('newapp.utils.newHideLoading 端能力不存在！！！');
    }
};

/**
 * @description 展示reload页面 大wifi
 */
const showReload = () => {
    if (boxx.canIUse('newapp.utils.showReload')) {
        boxx.newapp.utils.showReload({
            webviewId: window.webviewId || '',
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('newapp.utils.showReload 端能力不存在！！！');
    }
};

/**
 * @description 发送音效
 * @param type 音效类型 'send'|'end'
 */
const sendSound = (type: 'send' | 'end') => {
    if (boxx.canIUse('newapp.utils.sendSound')) {
        boxx.newapp.utils.sendSound({
            type,
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('newapp.utils.sendSound 端能力不存在！！！');
    }
};

/**
 * @description 调起反馈弹窗
 * @param messageId
 * @param type 操作状态： 点赞-like，点踩-dislike
 * @param feedbackData 反馈弹窗的选项信息
 * @param from 反馈面板来源
 */
const showFeedback = (params: object) => {
    if (boxx.canIUse('newapp.utils.showFeedback')) {
        boxx.newapp.utils.showFeedback({
            ...params,
            // success: (res: any) => {
            //     console.log('showFeedback success', res);
            // },
            // fail: (res: any) => {
            //     console.log('showFeedback fail', res);
            // },
        });
    }
    else {
        if (!inNewApp) {
            setTimeout(() => {
                const myEvent = new Event(NA_FEEDBACK_CLOSE);
                window.dispatchEvent(myEvent);
            }, 200);
        }
        else {
            console.error('newapp.utils.showFeedback 端能力不存在！！！');
        }
    }
};

/**
 * @description 获取公参
 */
const getCommonParams = (): Promise<string> => {
    return new Promise(resolve => {
        if (inNewApp && boxx.canIUse('newapp.utils.getCommonParams')) {
            boxx.newapp.utils.getCommonParams({
                url: 'https://getCommonParams',
                success: (res: ResOfCommonParams) => {
                    const {url, params} = res.data;
                    window.__commonParams = url
                        ? url.split('?')[1]
                        : params;
                    resolve(window.__commonParams);
                },
                fail: () => {
                    resolve(window.__commonParams || '');
                },
            });
        }
        else {
            resolve(window.__commonParams || '');
        }
    });
};

/**
 * @description 获取系统音量 http://es.baidu-int.com/#/apidetail/2273
 * @return Promise<string> 音量值 0 - 1 两位小数
 */
const getVolume = (): Promise<string> => {
    return new Promise((resolve, reject) => {
        if (boxx.canIUse('newapp.newaudio.getVolume')) {
            boxx.newapp.newaudio.getVolume({
                success: (res: ResOfGetVolume) => {
                    resolve(res.data.volume);
                },
                fail: (res: ResOfGetVolume) => {
                    reject(res.message);
                },
            });
        }
        else {
            console.error('newapp.newaudio.getVolume 端能力不存在！！！');
            reject('newapp.newaudio.getVolume 端能力不存在！！！');
        }
    });
};

/**
 * @description 获取鉴权信息
 */
const getAuthentication = (): Promise<string> => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.newaccount.getAuthentication') && inNewApp) {
            boxx.newapp.newaccount.getAuthentication({
                success: (res: AuthenticationRes) => {
                    resolve(res.data?.authentication || '文心');
                },
                fail: () => {
                    resolve('文心');
                },
            });
        }
        else {
            resolve('文心');
        }
    });
};

/**
 * @description 风险控制相关逻辑
 * @param sessionId 当前的sessionId
 * @param type 触犯类型，'session' | 'user'
 * @param chatState PC用
 */
const riskControl = (params: RiskControlParams = {}) => {
    if (!params.code) {
        params.code = 410;
    }

    // 鉴权相关的还走老的能力
    if (!ERRNO_LIST_AUTH.includes(params.code) && boxx.canIUse('newapp.newaccount.chatRiskControl')
    ) {
        const newParams = params?.tipKey as string in RISK_CONTROL_TIPKEY_MAP
            ? RISK_CONTROL_TIPKEY_MAP[params?.tipKey as string]
            : {};
        const {title, buttonTitle, ext, chatDisable} = params;
        // 处理动态title的情况，语音模式下会置为空字符串就不在3.5下展示
        if (typeof title === 'string') {
            newParams.title = title;
            newParams.toastMsg = title;
        }

        if (typeof buttonTitle === 'string') {
            newParams.buttonTitle = buttonTitle;
        }

        if (typeof chatDisable === 'number') {
            newParams.chatDisable = chatDisable;
        }

        // 大模型3.5下都可聊天
        if (ext?.modelId === MODELMAP.basic) {
            newParams.chatDisable = 0;
            delete ext.modelId;
        }

        newParams.ext = merge(newParams.ext, ext);
        boxx.newapp.newaccount.chatRiskControl({
            // 默认横版排列
            displayType: 0,
            ...newParams,
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else if (boxx.canIUse('newapp.newaccount.riskControl')) {
        boxx.newapp.newaccount.riskControl({
            // ios低版本校验参数，不传会校验失败
            message: '',
            sessionId: '',
            botId: '',
            ...params,
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('newapp.newaccount.riskControl 端能力不存在！！！');
        if (!inNewApp || isPcClient()) {
            __INSTANCE__.emitter?.emit('riskControl', {
                ...params,
            });
        }
    }
};
/**
 * @description 获取状态栏高度
 */
const getStatusBarHeight = (): Promise<number> => {
    return new Promise(resolve => {
        // 优先取window上注册的
        if (window.globalData?.statusBarHeight) {
            resolve(window.globalData.statusBarHeight);
        }
        else if (boxx.canIUse('newapp.screen.getStatusBarHeight') && inNewApp) {
            boxx.newapp.screen.getStatusBarHeight({
                success: (res: StatusBarHeightRes) => {
                    resolve(res.data?.height || 0);
                },
                fail: () => {
                    resolve(0);
                },
            });
        }
        else {
            console.error('newapp.screen.getStatusBarHeight 端能力不存在！！！');
            resolve(0);
        }
    });
};

/**
 * @description 获取系统设置信息 v1.4
 * @param types 获取的内容
 */
const getGlobalSettings = (types = [
    'aiId',
    'fontSize',
    'speakerId',
    'speakSpeed',
    'robots',
    'theme',
    'skin',
    'individual',
], forceUpdate = false): Promise<ResOfGetGlobalSettings> => {
    return new Promise(resolve => {
        // 优先取window上注册的
        // 参照：https://ku.baidu-int.com/knowledge/HFVrC7hq1Q/qCCPuIpbfn/2bDtTCu3yf/4FSHMzykCMG0uc
        if (window.globalData?.globalSettings && !forceUpdate) {
            resolve({
                status: 0,
                message: 'success',
                data: window.globalData.globalSettings,
            });
        }
        else if (appVersionCompare('1.4') >= 0 && boxx.canIUse('newapp.newsystem.getGlobalSettings')) {
            boxx.newapp.newsystem.getGlobalSettings({
                types,
                success: (res: ResOfGetGlobalSettings) => {
                    // 兼容android端能力返回皮肤状态不正确
                    if (isAndroid && window?.globalData?.globalSettings?.skin) {
                        resolve({
                            status: 0,
                            message: 'success',
                            data: {
                                individual: res.data?.individual,
                                ...window?.globalData?.globalSettings,
                            },
                        });
                    }
                    else {
                        resolve(res);
                    }
                },
                fail: () => {
                    resolve({
                        status: 1,
                        message: '1',
                        data: {},
                    });
                },
            });
        }
        else {
            resolve({
                status: 1,
                message: 'newapp.screen.getGlobalSettings 端能力不存在！！！',
                data: {},
            });
        }
    });
};

/**
 * @description 更新系统设置信息 v1.4
 * @param params 要更新的具体项及值
 */
const setGlobalSettings = (params: { [key: string]: string }): Promise<ResOfSetGlobalSettings> => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.newsystem.setGlobalSettings')) {
            boxx.newapp.newsystem.setGlobalSettings({
                ...params,
                success: (res: ResOfSetGlobalSettings) => {
                    resolve(res);
                },
                fail: () => {
                    resolve({
                        status: 1,
                        message: '1',
                        data: {},
                    });
                },
            });
        }
        else {
            resolve({
                status: 1,
                message: 'newapp.screen.setGlobalSettings 端能力不存在！！！',
                data: {},
            });
        }
    });
};

/**
 * @description FE通知端的通用方法 v1.5
 * @param params FeToNaData
 */
const feSendToNa = (params: FeToNaData): void => {
    if (boxx.canIUse('newapp.newsystem.feSendToNa')) {
        boxx.newapp.newsystem.feSendToNa({
            ...params,
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('newapp.screen.setGlobalSettings 端能力不存在！！！');
    }
};

/**
 * @description 跳转设置页 v1.4
 */
const invokeSettings = (): void => {
    if (boxx.canIUse('newapp.newsystem.invokeSettings')) {
        boxx.newapp.newsystem.invokeSettings({});
    }
    else {
        console.error('newapp.screen.invokeSettings 端能力不存在！！！');
    }
};

/**
 * @description 获取系统设置信息
 */
const getSettinInfo = (): Promise<ResOfGetSettingInfo> => {
    return new Promise(resolve => {
        // 优先取window上注册的
        if (window.globalData?.globalSettings) {
            resolve({
                status: 0,
                message: 'success',
                data: {
                    tts: window.globalData.globalSettings.tts || '0',
                },
            });
        }
        else if (boxx.canIUse('newapp.newsystem.getSettingInfo')) {
            boxx.newapp.newsystem.getSettingInfo({
                success: (res: ResOfGetSettingInfo) => {
                    resolve(res);
                },
                fail: () => {
                    resolve({
                        status: 1,
                        message: '1',
                        data: {
                            tts: '0',
                        },
                    });
                },
            });
        }
        else {
            resolve({
                status: 1,
                message: 'newapp.screen.getSettingInfo 端能力不存在！！！',
                data: {
                    tts: '0',
                },
            });
        }
    });
};

/**
 * @description 设置屏幕是否常亮
 * @param status 是否常亮 0 不常亮 1 常亮
 */
const keepDeviceAwake = (status: '0' | '1'): void => {
    // 全屏语音模式下控制权交给端上
    if (window.__settingInfo?.fullscreenVoiceMode) {
        return;
    }
    if (boxx.canIUse('newapp.screen.keepDeviceAwake')) {
        boxx.newapp.screen.keepDeviceAwake({
            status,
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
};

/**
 * @description tts、音频播放成功通知端可以收音
 *
 * @return  {void}    [return description]
 */
const audioIn = (): void => {
    if (window.webviewHide) {
        console.error('页面未展现');
        return;
    }
    if (boxx.canIUse('newapp.newaudio.audioIn')) {
        boxx.newapp.newaudio.audioIn({});
    }
    else {
        console.error('newapp.newaudio.audioIn 端能力不存在！！！');
    }
};

/**
 * @description 隐藏webview返回按钮
 */
const hideBackBtn = (): Promise<string> => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.navbar.hideBackBtn') && inNewApp) {
            boxx.newapp.navbar.hideBackBtn({
                success: (res: any) => {
                    resolve(res.status);
                },
                fail: () => {
                    resolve('0');
                },
            });
        }
        else {
            console.error('newapp.navbar.hideBackBtn 端能力不存在！！！');
            resolve('0');
        }
    });
};
/*
 * @description 灵感中心页面跳转到prompt会话
 * @description https://ku.baidu-int.com/knowledge/HFVrC7hq1Q/pKzJfZczuc/2bDtTCu3yf/YT39t8XlP59O5E
 * @param promptId prompt标识
 * @param promptName prompt标题
 */
const openPrompt = (params: OpenPromptParams) => {
    if (!params?.render) {
        delete params.render;
    }
    if (!params?.renderOperate) {
        delete params.renderOperate;
    }
    if (params?.plugin && Object.keys(params?.plugin).length === 0) {
        delete params.plugin;
    }
    params.isQuery = params?.isQuery?.toString();
    params.actionId = params?.actionId?.toString();
    params.pluginId = params?.pluginId?.toString();

    if (boxx.canIUse('newapp.newconversation.openprompt')) {
        boxx.newapp.newconversation.openprompt({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('openPrompt success', res);
            // },
            // fail: (res: any) => {
            //     console.log('openPrompt success',res);
            // },
        });
    }
    else {
        console.error('newapp.newconversation.openprompt 端能力不存在！！！');
    }
};

/*
 * @description 社区详情打开菜单面板
 * @description https://ku.baidu-int.com/knowledge/HFVrC7hq1Q/qCCPuIpbfn/2bDtTCu3yf/K0zx24j6DLqkc_
 * @param copyShare 生成链接的复制文案
 * @param isHost 是否是主态
 * @param deleteNid 要删除的nid
 */
const showMenu = (params: ShowMenu) => {
    if (boxx.canIUse('newbrowse.showMenu')) {
        boxx.newbrowse.showMenu({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('openPrompt success', res);
            // },
            // fail: (res: any) => {
            //     console.log('openPrompt success',res);
            // },
        });
    }
    else {
        // console.error('newbrowse.showMenu 端能力不存在！！！');
    }
};

/**
 * 把截图内容给NA进行展示
 * @param errno 生成成功0 失败1
 * @param key ios需要的放在window上的key
 * @param totalChunks ios需要的放在window上的key的数量totalChunks
 * @param shotImgUrl 安卓需要的图片url
 */
const sendScreenshotResult = (errno: 1 | 0, shotImgUrl = '', key = '__screenshotData', totalChunks = -1) => {
    if (boxx.canIUse('newbrowse.screenshotResult')) {
        boxx.newbrowse.screenshotResult({
            errno,
            key,
            totalChunks,
            shotImgUrl,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('openPrompt success', res);
            // },
            // fail: (res: any) => {
            //     console.log('openPrompt success',res);
            // },
        });
    }
    else {
        // console.error('newbrowse.screenshotResult 端能力不存在！！！');
    }
};

/**
 * 打开个人主页
 * @param uk 用户信息
 * @param type 锚点类型：create: 作品, like: 赞, prompt: 提示词, character: 角色
 * @param from 来源信息 默认homepage
 */
const openUserProfile = (uk: string, type = 'create', from = 'homepage') => {
    if (boxx.canIUse('newapp.userprofile.open')) {
        boxx.newapp.userprofile.open({
            uk,
            type,
            from,
        });
    }
    else {
        // console.error('newapp.userprofile.open 端能力不存在！！！');
    }
};

/**
 * @description 获取用户信息 http://es.baidu-int.com/#/apidetail/2353
 * @return Promise<string> 用户头像
 */
const getUserInfoFromNA = (): Promise<UserInfo> => {
    return new Promise(resolve => {
        if (boxx.canIUse('newaccount.getUserInfo')) {
            boxx.newaccount.getUserInfo({
                success: (res: ResOfGetUserInfo) => {
                    resolve(res.data);
                },
                fail: () => {
                    // 返回默认头像
                    resolve({
                        avatar: DEFAULT_USER_AVATAR,
                    });
                },
            });
        }
        else {
            resolve({
                avatar: DEFAULT_USER_AVATAR,
            });
        }
    });
};

/*
 * @description 社区详情页打开普通二级对话页
 * @description https://ku.baidu-int.com/knowledge/HFVrC7hq1Q/qCCPuIpbfn/2bDtTCu3yf/8ee10a4f288846
 * @param promptId prompt标识
 * @param promptName prompt标题
 */
const openChat = (params: OpenChatParams) => {
    if (params?.plugin && Object.keys(params?.plugin).length === 0) {
        delete params.plugin;
    }
    if (boxx.canIUse('newapp.newconversation.openchat')) {
        boxx.newapp.newconversation.openchat({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('openPrompt success', res);
            // },
            // fail: (res: any) => {
            //     console.log('openPrompt success',res);
            // },
        });
    }
    else {
        // console.error('newapp.newconversation.openchat 端能力不存在！！！');
    }
};

/**
 * 写入端的剪贴板
 *
 * @param text 写入的内容
 */
const writeClipboard = (text: string) => {
    if (boxx.canIUse('newsystem.writeClipboard')) {
        boxx.newsystem.writeClipboard({
            text,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('openPrompt success', res);
            // },
            // fail: (res: any) => {
            //     console.log('openPrompt success',res);
            // },
        });
    }
    else {
        // console.error('newsystem.writeClipboard 端能力不存在！！！');
        copyToClipBoard(text);
    }
};

/**
 * 告诉端上选区信息，目前就告诉安卓选区的id
 *
 * @param selectId 选区的wrapper的id，安卓端自己去选择内容
 */
const postSelectionInfo = (selectId: string) => {
    if (boxx.canIUse('newsystem.postSelectionInfo')) {
        boxx.newsystem.postSelectionInfo({
            selectId,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('openPrompt success', res);
            // },
            // fail: (res: any) => {
            //     console.log('openPrompt success', res);
            // },
        });
    }
    else {
        // console.error('newsystem.postSelectionInfo 端能力不存在！！！');
    }
};

const detailPostSelectionInfo = (selectId: string) => {
    if (boxx.canIUse('newbrowse.postSelectionInfo') && appVersionCompare('4.1') >= 0) {
        boxx.newbrowse.postSelectionInfo({
            selectId,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('newbrowse.postSelectionInfo', res);
            // },
            // fail: (res: any) => {
            //     console.log('newbrowse.postSelectionInfo',res);
            // }
        });
    }
    else {
        // console.error('newbrowse.postSelectionInfo 端能力不存在！！！');
    }
};

const detailContextMenu = (params: { disable: boolean }) => {
    const {disable} = params;
    if (boxx.canIUse('newbrowse.contextMenu') && appVersionCompare('4.17') >= 0 && isAndroid) {
        boxx.newbrowse.contextMenu({
            disable: disable ? 1 : 0,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('newbrowse.postSelectionInfo', res);
            // },
            // fail: (res: any) => {
            //     console.log('newbrowse.postSelectionInfo',res);
            // }
        });
    }
    else {
        // console.error('newbrowse.postSelectionInfo 端能力不存在！！！');
    }
};

/**
 * 获取互动消息数据
 * @param type 消息类型：0-全部 1-关注 2-点赞
 * @param count 每次拉取的数据量，默认20条
 * @param msgid 已经拉取的最后一条消息的id，第一次拉取传0
 */
const getInteractionData = (type = 0, count = 20, msgid = '0') => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.interaction.getMsgs')) {
            boxx.newapp.interaction.getMsgs({
                type,
                count,
                msgid,
                success: (res: any) => {
                    resolve(res.data);
                },
                fail: () => {
                    resolve('0');
                },
            });
        }
        else {
            // console.error('newapp.interaction.getMsgs 端能力不存在！！！');
            resolve('0');
        }
    });
};

/**
 * 连续包月退订
 * @param dialog 对话框信息
 * @param isAppleIAP
 * @param productId
 */
const cancelMonthlyPay = (
    dialog: AgreementCancelDialogParams, isAppleIAP: string, productId: string) => {
    return new Promise((resolve, reject) => {
        if (boxx.canIUse('newapp.newpay.cancelMonthlyPay')) {
            boxx.newapp.newpay.cancelMonthlyPay({
                dialog,
                isAppleIAP,
                productId,
                success: (res: any) => {
                    resolve(res.data);
                },
                fail: () => {
                    console.error('退订失败');
                    reject(false);
                },
            });
        }
        else {
            reject(false);
            console.error('newapp.net.request 端能力不存在');
        }
    });
};

/**
 * @description 展示loading
 */
const openLoading = (params = {}) => {
    if (boxx.canIUse('newapp.utils.newShowLoading')) {
        boxx.newapp.utils.newShowLoading({
            ...params,
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        // console.error('newapp.utils.newShowLoading 端能力不存在！！！');
    }
};

/**
 * 告诉端上进去分享状态
 */

interface createShareParams {
    isHasVideo?: boolean;
    itemList?: SHARE_PANEL_ITEM[];
};
const createShare = (params: createShareParams) => {
    if (boxx.canIUse('newsystem.createShare')) {
        boxx.newsystem.createShare({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('newsystem.createShare success', res);
            // },
            // fail: (res: any) => {
            //     console.log('newsystem.createShare success', res);
            // },
        });
    }
    else {
        // console.error('newsystem.createShare 端能力不存在！！！');
    }
};

/**
 * 互动消息跳转二级页
 * @param source 来源信息
 */
const openInteraction = (source: any) => {
    if (boxx.canIUse('newapp.interaction.click')) {
        boxx.newapp.interaction.click({
            source,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('openPrompt success', res);
            // },
            // fail: (res: any) => {
            //     console.log('openPrompt success',res);
        });
    }
    else {
        // console.error('newapp.interaction.click 端能力不存在！！！');
    }
};

/**
 * 互动消息跳转二级页
 * @param source 来源信息
 */
const openCommunityDetail = (params: any) => {
    if (boxx.canIUse('newapp.community.detail')) {
        boxx.newapp.community.detail({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('openPrompt success', res);
            // },
            // fail: (res: any) => {
            //     console.log('openPrompt success',res);
            // }
        });
    }
    else {
        // console.error('newapp.interaction.click 端能力不存在！！！');
    }
};

/**
 * 互动消息全部已读
 */
const setAllMsgRead = () => {
    // 4.2版本后改为端上已读，不用调这个
    if (boxx.canIUse('newapp.interaction.setAllMsgRead') && appVersionCompare('4.2') < 0) {
        boxx.newapp.interaction.setAllMsgRead({
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        // console.error('newapp.interaction.setAllMsgRead 端能力不存在！！！');
    }
};

const sendPageTrack = (params: SendPageTrack) => {
    if (boxx.canIUse('newapp.chat.pageTrack')) {
        boxx.newapp.chat.pageTrack({
            ...params,
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        // console.error('newapp.chat.pageTrack 端能力不存在！！！');
    }
};

/**
 * 预览页获取容器高度
 *
 * @param status 是否渲染成功
 * @param height 容器高度
 */
const getPreviewHeight = (status: string, height = 0) => {
    if (boxx.canIUse('whpublish.getPreviewHeight')) {
        boxx.whpublish.getPreviewHeight({
            status,
            height,
        });
    }
    else {
        // console.error('whpublish.getPreviewHeight 端能力不存在！！！');
    }
};

/**
 * 打开分享面板
 *
 * @param params 入参
 */
const openShareDialog = (params: ShareDialog) => {
    if (boxx.canIUse('newapp.newshare.shareDialog')) {
        boxx.newapp.newshare.shareDialog({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('newsystem.createShare success', res);
            // },
            // fail: (res: any) => {
            //     console.log('newsystem.createShare success', res);
            // },
        });
    }
    else {
        console.error('newapp.share.shareDialog 端能力不存在！！！');
    }
};

/**
 * im页fe发送消息给端
 *
 * @param   {sendMessage}  params  参数
 */
const sendMessageToNA = (params: SendMessageData) => {
    if (boxx.canIUse('newapp.chat.fe2na')) {
        boxx.newapp.chat.fe2na({
            ...params,
            // success: (res: any) => {
            //     console.info('success>>>', res);
            // },
            // fail: (res: any) => {
            //     console.info('fail>>>', res);
            // },
        });
    }
    else {
        console.error('newapp.chat.fe2na 端能力不存在！！！');
    }
};

/**
 * 获取页面是一级页面还是二级页面
 *
 * @return  {Promise<string>}[return 'main_chat' | 'second_chat' | 'unknown']
 */
const getHostTag = (): Promise<string> => {
    return new Promise(resolve => {
        // 优先取window上注册的
        if (window.globalData?.hostTag) {
            resolve(window.globalData.hostTag);
        }
        // 优先取config上注册的
        else if (__INSTANCE__?.config?.hostTag) {
            resolve(__INSTANCE__.config.hostTag);
        }
        else if (boxx.canIUse('newapp.utils.getHostTag')) {
            boxx.newapp.utils.getHostTag({
                success: (res: GetHostTagResponse) => {
                    // 这里不能将 hostTag 赋值给 globalData，在多摸 webview 复用场景下会有问题
                    resolve(res.data.tag);
                },
                fail: () => {
                    resolve(PAGE_TYPE.UNKNOWN);
                },
            });
        }
        else if (!inNewApp || isPcClient()) {
            resolve(PAGE_TYPE.SECOND);
        }
        else {
            console.error('newapp.utils.getHostTag 端能力不存在！！！');
            resolve(PAGE_TYPE.UNKNOWN);
        }
    });
};

/**
 * 打开伙伴设置页
 *
 * @param  characterId 伙伴id
 */
const openCharacterSettings = (params: OpenCharacterSettingsParams) => {
    if (boxx.canIUse('newapp.character.openCharacterDetail')) {
        boxx.newapp.character.openCharacterDetail({
            ...params,
            // success: (res: any) => {
            //     console.info('success>>>', res);
            // },
            // fail: (res: any) => {
            //     console.info('fail>>>', res);
            // },
        });
    }
    else {
        console.error('newapp.character.openCharacterDetail 端能力不存在！！！');
    }
};

/**
 * 打开伙伴、prompt详情页
 * @param url 链接
 */
const openDetail = (params: OpenDetailParams) => {
    if (boxx.canIUse('newapp.newbrowse.detail')) {
        boxx.newapp.newbrowse.detail({
            ...params,
            // success: (res: any) => {
            //     console.info('success>>>', res);
            // },
            // fail: (res: any) => {
            //     console.info('fail>>>', res);
            // },
        });
    }
    else if (pcClientCanIUse('native:newbrowse-detail')) {
        window.native.send('native:newbrowse-detail', params);
    }
    else {
        console.error('newapp.newbrowse.detail 端能力不存在！！！');
    }
};

/**
 * 社区详情页给NA传底bar信息
 * @param url 链接
 */
const sendCommunityInfo = (params: CommunityInfo) => {
    if (boxx.canIUse('newapp.newcommunity.setCommunityInfo')) {
        boxx.newapp.newcommunity.setCommunityInfo({
            ...params,
            // success: (res: any) => {
            //     console.info('success>>>', res);
            // },
            // fail: (res: any) => {
            //     console.info('fail>>>', res);
            // },
        });
    }
    else {
        console.error('newapp.newcommunity.setCommunityInfo 端能力不存在！！！');
    }
};

/**
 * 一言图表保存到本地
 *
 * @param key chunk的key
 * @param totalChunks chunk总数
 */
const sendChartImageData = (key: string, totalChunks: number) => {
    if (boxx.canIUse('newapp.utils.downloadPicture')) {
        boxx.newapp.utils.downloadPicture({
            key,
            totalChunks,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('newsystem.createShare success', res);
            // },
            // fail: (res: any) => {
            //     console.log('newsystem.createShare success', res);
            // },
        });
    }
    else {
        console.error('newapp.utils.downloadPicture 端能力不存在！！！');
    }
};

/**
 * newapp跳转主对话
 * ES: https://es.baidu-int.com/index#/apidetail/2481/params
 *
 */
const routeMainChat = (params?: {
    modelId?: string;
    successTip?: string;
    startInput?: string;
}) => {
    const {modelId, successTip = '', startInput} = params || {};
    if (boxx.canIUse('newapp.newconversation.routeMainChat')) {
        boxx.newapp.newconversation.routeMainChat({
            modelId,
            ...successTip && {
                'success_tip': successTip,
            },
            ...startInput && {
                'start_input': startInput,
            },
        });
    }
    else {
        console.error('newapp.newconversation.routeMainChat 端能力不存在！！！');
    }
};

/**
 * Android_获取钱包支付渠道
 * ES：https://es.baidu-int.com/index#/apidetail/2473/invoke
 *
 */
interface GetQequestChannelListParams {
    nativeAppId: string;
    channelSource: string;
    totalAmount: string;
    dealId: string;
    appKey: string;
}
interface GetQequestChannelListResponse {
    data: any;
}
const getQequestChannelList = (params: GetQequestChannelListParams) => {
    return new Promise((resolve, reject) => {
        if (boxx.canIUse('newapp.newpay.requestChannelList')) {
            boxx.newapp.newpay.requestChannelList({
                ...params,
                success: (res: GetQequestChannelListResponse) => {
                    if (res?.data?.content) {
                        resolve(JSON.parse(res.data.content));
                    }
                    else {
                        reject();
                    }

                },
                fail: () => {
                    reject();
                },
            });
        }
        else if (!inNewApp) {
            reject();
        }
        else {
            console.error('newapp.newpay.requestChannelList 端能力不存在！！！');
            reject();
        }
    });
};

/**
 * newapp 调起支付
 * ES：https://es.baidu-int.com/index#/apidetail/2474/params
 */
interface StartPayParams {
    ext: {
        [key: string]: string;
    };
    protocolDialog: {
        reaskMsg: string;
        okButton: string;
        msg: string;
        title: string;
    };
    chosenChannel?: string;
    isCheckProtocol: string;
    source?: string;
    productName: string;
    productId: string;
    count: string;
    totalAmount: string;
}

interface GetStartPayResponse {
    data: {
        code: '0' | '1' | 'unknown';
    };
}

interface QuestionEditParams {
    id: string;
    url: string;
    width: string;
    height: string;
    pluginId: string;
    actionId: string;
    sessionId: string;
    rectIndex: string;
    rectList: {
        left: number;
        top: number;
        width: number;
        height: number;
    };
}
const getStartPay = (params: StartPayParams) => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.newpay.startPay')) {
            boxx.newapp.newpay.startPay({
                ...params,
                success: (res: GetStartPayResponse) => {
                    resolve(res.data.code);
                },
                fail: () => {
                    resolve('unknown');
                },
            });
        }
        else if (!inNewApp) {
            resolve('second_chat');
        }
        else {
            console.error('newapp.newpay.startPay 端能力不存在！！！');
            resolve('unknown');
        }
    });
};

/**
 * 跳转系统应用push设置页面
 *
 */
const invokeSystemPushSettings = () => {
    if (boxx.canIUse('newapp.newsystem.invokeSystemPushSettings')) {
        boxx.newapp.newsystem.invokeSystemPushSettings({});
    }
    else {
        console.error('newapp.newsystem.invokeSystemPushSettings 端能力不存在！！！');
    }
};

/**
 * 获取用户推送权限
 */
const getNotificationStatus = () => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.newsystem.getNotificationStatus')) {
            boxx.newapp.newsystem.getNotificationStatus({
                success: (res: any) => {
                    resolve(res?.data?.status || '1');
                },
                fail: () => {
                    resolve('1');
                },
            });
        }
        else {
            if (isPcClient()) {
                resolve('1');
                return;
            }
            console.error('newapp.newsystem.getNotificationStatus 端能力不存在！！！');
            // 调不起默认已开启
            resolve('1');
        }
    });

};

/**
 * 点击调起题目图片编辑器
 * @param params
 */
const invokeQuestionEdit = (params: QuestionEditParams) => {
    if (boxx.canIUse('newapp.newphoto.invokeQuestionEdit')) {
        boxx.newapp.newphoto.invokeQuestionEdit({
            ...params,
            // success: (res: any) => {
            //     console.info('invokeQuestionEdit success>>>', res);
            // },
            // fail: (res: any) => {
            //     console.info('invokeQuestionEdit fail>>>', res);
            // },
        });
    }
    else {
        if (isHarmonyOS) {
            showNaToast('该功能暂不支持');
        }
        console.error('newapp.newphoto.invokeQuestionEdit 端能力不存在！！！');
    }
};

/**
 * @description 灵感币任务页，吊起灵感币支付面板
 */
const invokePaymentPanel = () => {
    if (boxx.canIUse('newapp.newpay.showPaymentPannel')) {
        boxx.newapp.newpay.showPaymentPannel();
    }
    else {
        console.error('newapp.newpay.showPaymentPannel 端能力不存在！！！');
    }
};

/**
 * @description 通知页，吊起菜单面板
 */
const invokeMenuPannel = () => {
    if (boxx.canIUse('newapp.newHome.showMenuPannel')) {
        boxx.newapp.newHome.showMenuPannel();
    }
    else {
        console.error('newapp.newHome.showMenuPannel 端能力不存在！！！');
    }
};

/**
 * @description 吊起版本升级面板（安卓专用）
 */
const appUpgrade = () => {
    if (boxx.canIUse('newapp.utils.appUpgrade')) {
        boxx.newapp.utils.appUpgrade();
    }
    else {
        console.error('newapp.utils.appUpgrade 端能力不存在！！！');
    }
};

/**
 * @description 通知页，吊起搜索面板
 */
const invokeSearchPannel = () => {
    if (boxx.canIUse('newapp.newHome.showSearchPannel')) {
        boxx.newapp.newHome.showSearchPannel();
    }
    else {
        console.error('newapp.newHome.showSearchPannel 端能力不存在！！！');
    }
};

/**
 * @description 通知页 获取顶bar icon信息
 */
const getIconInfo = (): Promise<iconInfo> => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.newHome.getIconInfo')) {
            boxx.newapp.newHome.getIconInfo({
                success: (res: BarIconInfoRes) => {
                    resolve(res.data || {});
                },
                fail: () => {
                    resolve({});
                },
            });
        }
        else {
            console.error('newapp.newHome.getIconInfo 端能力不存在！！！');
            resolve({});
        }
    });
};

/**
 * 获取统计参数
 *
 * @return  {Promise<string>}
 */

interface GetFrameDataResponse {
    data: {
        frameExt: string;
    };
}
const getFrameData = (): Promise<object> => {
    return new Promise(resolve => {
        // 优先取window上注册的
        // 安卓没有值的情况下会是空字符串，所以判断非undefined
        if (window.globalData?.frameExt !== undefined) {
            try {
                const frameDataJson = JSON.parse(window.globalData.frameExt);
                resolve(frameDataJson);
            }
            catch {
                resolve({});
            }
        }
        else if (boxx.canIUse('newapp.newbrowse.getFrameData')) {
            boxx.newapp.newbrowse.getFrameData({
                success: (res: GetFrameDataResponse) => {
                    if (res.data?.frameExt) {
                        const frameDataJson = JSON.parse(res.data.frameExt);
                        resolve(frameDataJson);
                    }
                    resolve({});
                },
                fail: () => {
                    resolve({});
                },
            });
        }
        else if (!inNewApp || isPcClient()) {
            resolve({});
        }
        else {
            console.error('newapp.newbrowse.getFrameData 端能力不存在！！！');
            resolve({});
        }
    });
};

/**
 * @description 吊起全屏语音面板
 */
const openFullScreenVoiceChat = (params = {}) => {
    Object.assign(params, {
        webviewId: window.webviewId,
    });
    if (boxx.canIUse('newapp.newconversation.openFullScreenVoiceChat')) {
        boxx.newapp.newconversation.openFullScreenVoiceChat(params);
    }
    else {
        console.error('newapp.newconversation.openFullScreenVoiceChat 端能力不存在！！！');
    }
};

/**
 * @description 吊起付费解锁面板
 */
const openModelUnlockPanel = (params = {}, source = 'click') => {
    if (boxx.canIUse('newapp.newconversation.openModelUnlockPanel')) {
        boxx.newapp.newconversation.openModelUnlockPanel(
            {
                ...params,
                source,
            }
        );
    }
    else {
        console.error('newapp.newconversation.openModelUnlockPanel 端能力不存在！！！');
    }
};

/**
 * @description 打开NA的视频播放器
 * @description https://es.baidu-int.com/index#/apidetail/2571
 * @param idList 当前的对话id string
 * @param src 视频链接地址 string
 * @param poster 视频封面图片的连接 string
 */

interface OpenVideoPlayerParams {
    idList: string;
    src: string;
    poster?: string;
    aiAvatar?: string;
    aiName?: string;
    videoWidth?: string;
    videoHeight?: string;
    type?: string;
    title?: string;
    time?: string;
    statistics?: object;
    shareContent?: string;
}
/**
 * @description 打开NA的视频播放器
 */
const openNAVideo = (params: OpenVideoPlayerParams) => {
    if (boxx.canIUse('newapp.newsystem.openNAVideo')) {
        boxx.newapp.newsystem.openNAVideo({
            ...params,
        });
    }
    else if (pcClientCanIUse('native:newsystem-openNAVideo')) {
        window.native.send('native:newsystem-openNAVideo', params);
    }
    else {
        if (isHarmonyOS) {
            showNaToast('该功能暂不支持');
        }
        console.error('newapp.newsystem.openNAVideo 端能力不存在！！！');
    }
};

interface OpenFlowVideoParams {
    avatarUrl?: string;
    nickName?: string;
    isSupportChat: number;
    isSupportRecommend: number;
    scene: string;
    hostTag: string;
    context?: string[];
    characterName?: string;
    characterId?: string;
    chatIconUrl?: string;
    position: number;
    total: number;
    messageId: string;
    askType?: string;
    isHistory?: string;
    sug: Array<{
        box: string;
        screen: string;
        type: number;
        ext: {
            'rec_query_type': number;
        };
    }>;
    first: {
        data: {
            shareData?: Record<string, any>;
            [x: string]: any;
        };
        id: string;
        layout: string;
    };
}
/**
 * @description 打开沉浸态落地页
 * https://es.baidu-int.com/#/apidetail/2709
 */
const openNAFlowVideo = (params: OpenFlowVideoParams) => {
    if (boxx.canIUse('newapp.recommendflow.openLandingPage')) {
        boxx.newapp.recommendflow.openLandingPage({
            ...params,
        });
    }
    else if (pcClientCanIUse('native:recommendflow-openLandingPage')) {
        const layout = params?.first?.layout;
        // PC端不支持图文沉浸态，直接返回，layout枚举值：news、dynamic、video
        if (layout === 'news' || layout === 'dynamic') {
            return showNaToast('暂不支持，请前往文心APP查看消息');
        }
        window.native.send('native:recommendflow-openLandingPage', params);
    }
    else {
        if (isHarmonyOS) {
            showNaToast('该功能暂不支持');
        }
        console.error('newapp.recommendflow.openLandingPage 端能力不存在！！！');
    }
};

/**
 * @description 给NA发送倒计时信息
 */
const sendDayCardCountDown = (params = {}) => {
    if (boxx.canIUse('newapp.newconversation.dayCardCountDown')) {
        boxx.newapp.newconversation.dayCardCountDown(params);
    }
    else {
        console.error('newapp.newconversation.dayCardCountDown 端能力不存在！！！');
    }
};

const playOfflineTTS = (params = {}) => {
    if (boxx.canIUse('newapp.newtts.tryPlayOffline')) {
        boxx.newapp.newtts.tryPlayOffline({
            ...params,
        });
    }
};

const playOnlineTTS = (params = {}) => {
    if (boxx.canIUse('newapp.newtts.play')) {
        boxx.newapp.newtts.play({
            ...params,
        });
    }
};

const createGreetingsVideo = (params = {}) => {
    if (boxx.canIUse('newapp.newVideo.createGreetingsVideo')) {
        boxx.newapp.newVideo.createGreetingsVideo({
            ...params,
        });
    }
};

const isSupportVirtualMan = () => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.virtualMan.isSupportVirtualMan')) {
            boxx.newapp.virtualMan.isSupportVirtualMan({
                success: (res: any) => {
                    const data = res?.data || {};
                    resolve(data);
                },
                fail: (res: any) => {
                    console.error('inewapp.virtualMan.isSupportVirtualMan fail>>>', res);
                    resolve({});
                },
            });
        }
        else {
            console.error('newapp.virtualMan.isSupportVirtualMan 端能力不存在！！！');
            resolve({});
        }
    });
};
/**
 * @description 打开图片选择器并上传
 */
const openImagePickerAndUpload = (params = {}) => {
    if (boxx.canIUse('newapp.newbrowse.openImagePickerAndUpload')) {
        boxx.newapp.newbrowse.openImagePickerAndUpload(params);
    }
    else {
        console.error('newapp.newconversation.openImagePickerAndUpload 端能力不存在！！！');
    }
};

/**
 * @description 上传照片
 */
const uploadImage = (params = {}) => {
    if (boxx.canIUse('newapp.newbrowse.uploadImage')) {
        boxx.newapp.newbrowse.uploadImage({
            ...params,
            // success: (res: any) => {
            //     console.log('newapp.newbrowse.uploadImage success', res);
            // },
            // fail: (res: any) => {
            //     console.log('newapp.newbrowse.uploadImage fail', res);
            // },
        });
    }
    else {
        console.error('newapp.newbrowse.uploadImage 端能力不存在！！！');
    }
};

/**
 * @description 删除照片
 */
const deleteImage = (params = {}) => {
    if (boxx.canIUse('newapp.newbrowse.deleteImage')) {
        boxx.newapp.newbrowse.deleteImage(params);
    }
    else {
        console.error('newapp.newconversation.deleteImage 端能力不存在！！！');
    }
};

/**
 * @description 跨webview上屏
 * @param text 上屏的文字
 * @param sourceWebviewId
 * @param extra 上屏的额外信息
 */
const uploadCompleteAndSendToChat = (params: {
    text: string;
    sourceWebviewId: string;
    extra: string;
}) => {
    if (boxx.canIUse('newapp.newbrowse.imageUploadCompleteAndSendToChat')) {
        boxx.newapp.newbrowse.imageUploadCompleteAndSendToChat({
            ...params,
            // success: (res: any) => {
            //     console.log('newapp.newbrowse.imageUploadCompleteAndSendToChat success', res);
            // },
            // fail: (res: any) => {
            //     console.log('newapp.newbrowse.imageUploadCompleteAndSendToChat fail', res);
            // },
        });
    }
    else {
        console.error('newapp.newbrowse.imageUploadCompleteAndSendToChat 端能力不存在！！！');
    }
};

/**
 * 告诉端上要进入生成视频状态
 */
const createVideo = () => {
    if (boxx.canIUse('newapp.newsystem.createVideo')) {
        boxx.newapp.newsystem.createVideo({
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('newapp.newsystem.createVideo success', res);
            // },
            // fail: (res: any) => {
            //     console.log('newapp.newsystem.createVideo success', res);
            // },
        });
        // stopTtsAndNotifyNa();
    }
    else {
        console.error('newapp.newsystem.createVideo 端能力不存在！！！');
    }
};

/**
 * @description 获取用户信息 https://es.baidu-int.com/index?#/apidetail/2611/base
 * @param {number} ttsStatus ttsStatus取值：1 已创建，2 排队中, 3 训练中，4 成功，5 失败，6 不可播放
 * @param {string} md5 语音包md5值
 * @param {string} mid 语音包mid值
 * @param {string} cmd 服务端控制语音包类型,1、ball 2、vis/xx 3、visOnline
 * @return NewApp获取个性语音包下载状态,0，未下载；1，已下载
 */

const checkModelStatusFromNA = (digitVoice: DigitVoiceData = {}) => {
    // 删除cmd，避免digitVoice在上游tts播放时设置多余参数
    delete digitVoice.cmd;
    const {ttsStatus, mid, md5, ttsStatusV2 = 1} = digitVoice;
    if (appVersionCompare('1.14') < 0 || isShowUgcTip || !ttsStatus) {
        return;
    }
    if (ttsStatus === TTS_STATE_CONFIG.NOT_PLAYABLE) {
        isShowUgcTip = true;
        showNaToast('请升级新版APP，体验新的语音播报');
        return;
    }
    if (ttsStatus === TTS_STATE_CONFIG.SUCCESS && boxx.canIUse('newapp.newtts.checkModelStatus')
    ) {
        boxx.newapp.newtts.checkModelStatus({
            mid,
            md5,
            success: (res: ResModelStatus) => {
                // 新版tts状态为成功，且新版tts端能力存在，则不显示提示
                if (ttsStatusV2 === TTS_STATE_CONFIG.SUCCESS && appVersionCompare('2.3.0.0') >= 0) {
                    return;
                }
                if (res.data && res.data?.status === '0') {
                    isShowUgcTip = true;
                    showNaToast('新语音生成中，成功后将进行更新');
                }
            },
        });
        return;
    }
    const isCreatingStatus = [
        TTS_STATE_CONFIG.CREATE,
        TTS_STATE_CONFIG.QUEUING,
        TTS_STATE_CONFIG.TRAINING,
    ];
    const ttsV1IsCreating = isCreatingStatus.includes(ttsStatus);
    const ttsV2IsCreating = isCreatingStatus.includes(ttsStatusV2);
    if ((ttsV1IsCreating && ttsV2IsCreating)) {
        isShowUgcTip = true;
        showNaToast('新语音生成中，成功后将进行更新');
    }
};

/**
 * @description 给NA发送聊天信息，存储用于本地搜索
 */
const saveMsg = (params = {}) => {
    if (boxx.canIUse('newapp.chat.saveMsg') && appVersionCompare('3.0') >= 0) {
        boxx.newapp.chat.saveMsg(params);
    }
    else {
        if (isPcClient()) {
            return;
        }
        console.error('newapp.chat.saveMsg 端能力不存在！！！');
    }
};

interface DeleteMessageData {
    parentId?: string;
    role?: 0 | 1;
    sessionId?: string;
    sessionType?: string;
    messageId: string;
    boxId?: number;
}

interface DeleteMsgParams {
    lastTime?: number;
    msgs?: DeleteMessageData[];
    type?: 'msg' | 'history';
    characterInfo?: {
        botId: string;
        characterId?: string;
        promptId?: string;
    };
}

/**
 * @description 给NA发送通知，删除本地存储的数据
 */
const deleteMsg = (params: DeleteMsgParams) => {
    if (boxx.canIUse('newapp.chat.deleteMsg') && appVersionCompare('4.6') >= 0) {
        boxx.newapp.chat.deleteMsg(params);
    }
    else {
        console.error('newapp.chat.deleteMsg 端能力不存在！！！');
    }
};

/*
 * @description 调用地图确认面板
 * ES: https://es.baidu-int.com/#/apidetail/2622
 */
const showMapPannel = (params = {}) => {
    return new Promise((resolve, reject) => {
        if (boxx.canIUse('newapp.location.showMapPannel')) {
            boxx.newapp.location.showMapPannel({
                ...params,
                success: (res: any) => {
                    resolve(res.data);
                },
                fail: () => {
                    reject(false);
                },
            });
        }
        else {
            console.error('newapp.location.showMapPannel 端能力不存在！！！');
        }
    });
};

/**
 * @description 调用地理位置授权面板
 * ES: https://es.baidu-int.com/#/apidetail/2625
 */
const authorizeLocation = (params = {}) => {
    if (boxx.canIUse('newapp.location.authorizeLocation')) {
        boxx.newapp.location.authorizeLocation(params);
    }
    else {
        console.error('newapp.location.authorizeLocation 端能力不存在！！！');
    }
};

/**
 * @description 通知地理位置引导按钮已显示
 * ES: https://es.baidu-int.com/#/apidetail/2631
 */
const authorizeLocationDisplayed = (params = {}) => {
    if (boxx.canIUse('newapp.location.authorizeLocationDisplayed')) {
        boxx.newapp.location.authorizeLocationDisplayed(params);
    }
    else {
        console.error('newapp.location.authorizeLocationDisplayed 端能力不存在！！！');
    }
};

/**
 * @description 视频预取
 * ES: https://es.baidu-int.com/#/apidetail/2677/params
 */
const prefetchNAVideo = (url: string) => {
    if (boxx.canIUse('newapp.newsystem.prefetchNAVideo')) {
        boxx.newapp.newsystem.prefetchNAVideo({
            prefetchURL: url,
        });
    }
    else {
        console.error('newapp.newsystem.prefetchNAVideo 端能力不存在！！！');
    }
};

/**
 * @description 打开小米应用商店
 * ES: https://es.baidu-int.com/index#/apidetail/2651
 */
const invokeMiMarket = (params = {}) => {
    return new Promise((resolve, reject) => {
        if (boxx.canIUse('newapp.utils.invokeMiMarket')) {
            boxx.newapp.utils.invokeMiMarket({
                ...params,
                success: (res: any) => {
                    resolve(res.data);
                },
                fail: () => {
                    reject(false);
                },
            });
        }
        else {
            console.error('newapp.location.invokeMiMarket 端能力不存在！！！');
            reject(false);
        }
    });
};
/**
 * @description 判断是否是小米手机
 * ES https://es.baidu-int.com/index#/apidetail/2674
 */
const isMiMobile = () => {
    return new Promise((resolve, reject) => {
        if (boxx.canIUse('newapp.utils.isMiMobile')) {
            boxx.newapp.utils.isMiMobile({
                success: (res: any) => {
                    resolve(res?.data?.isMiMobile);
                },
                fail: () => {
                    reject(false);
                },
            });
        }
        else {
            console.error('newapp.location.isMiMobile 端能力不存在！！！');
            reject(false);
        }
    });
};
/**
 * @description 五图上传
 * @param params images 图片列表
 * @param sizeLimit 图片大小
 * @param examples
 * @param needDirectOpenImagesSelectPage 是否直接吊起半屏相册 1-直接吊起 0-原来逻辑
 * @param title 主标题
 * @param descTitle 描述
 * @returns
 */
const openAIFigureUpload = (params: {
    images: Array<{ id: number; url: string }>;
    sizeLimit?: number;
    examples?: string[];
    needDirectOpenImagesSelectPage?: number;
    title?: string;
    descTitle?: string;
    appId?: number;
}) => {
    return new Promise((resolve, reject) => {
        if (boxx.canIUse('newapp.newbrowse.aiFigureUpload')) {
            boxx.newapp.newbrowse.aiFigureUpload({
                ...params,
                success: (res: any) => {
                    resolve(res);
                },
                fail: () => {
                    reject(false);
                },
            });
        }
        else {
            console.error('newapp.newbrowse.aiFigureUpload 端能力不存在！！！');
            reject(false);
        }
    });
};

/**
 * @description 保存图片到相册
 * @description https://es.baidu-int.com/index#/apidetail/2692/base
 * @param url 需要保存的图片url
 * @param logParams 客户端需要的打点参数
 */
const saveImageToAlbum = (params: {
    url: string;
    waterMarkUrl?: string;
    logParams?: { params?: Record<string, any>; ext?: Record<string, any> };
    successPushScheme?: string;
}) => {
    if (boxx.canIUse('newapp.newsystem.saveImage') && appVersionCompare('3.2') >= 0) {
        boxx.newapp.newsystem.saveImage({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('newapp.newsystem.saveImage success', res);
            // },
            // fail: (res: any) => {
            //     console.log('newapp.newsystem.saveImage fail', res);
            // },
        });
    }
    else if (pcClientCanIUse('native:newsystem-saveImage')) {
        window.native.send('native:newsystem-saveImage', params);
    }
    else {
        if (isHarmonyOS) {
            showNaToast('该功能暂不支持');
        }
        console.error('newapp.newsystem.saveImage 端能力不存在！！！');
    }
};

/**
 * @description 调起分享图片菜单
 * @description https://es.baidu-int.com/index#/apidetail/2694/base
 * @param url 需要分享的图片url
 * @param logParams 客户端需要的打点参数
 */
const shareImage = (params: {
    url: string;
    logParams?: { params?: Record<string, any>; ext?: Record<string, any> };
}) => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.newsystem.shareImage') && appVersionCompare('3.2') >= 0 && !isHarmonyOS) {
            boxx.newapp.newsystem.shareImage({
                ...params,
                // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
                // success: () => {
                //     console.log('newapp.newsystem.shareImage success', res);
                // },
                // fail: () => {
                //     console.log('newapp.newsystem.shareImage fail', res);
                // },
            });
        }
        else {
            if (isHarmonyOS) {
                showNaToast('该功能暂不支持');
            }
            console.error('newapp.newsystem.shareImage端能力不存在！！！');
            resolve(0);
        }
    });
};

/**
 * @description 获取手机是否安装了微信（仅iOS支持）
 * @return Promise<number> 0:未安装，1：已安装
 */
const getWechatInstalled = (): Promise<number> => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.newsystem.getWechatInstalled') && appVersionCompare('3.2') >= 0) {
            boxx.newapp.newsystem.getWechatInstalled({
                success: (res: GetWechatInstalledRsp) => {
                    resolve(res.data.installed);
                },
                fail: () => {
                    resolve(0);
                },
            });
        }
        else {
            console.error('newapp.newsystem.getWechatInstalled 端能力不存在！！！');
            resolve(0);
        }
    });
};

/**
 * @description 调起表格下载or分享菜单
 */
const showDownloadMenu = (params: {
    title: string;
    items: object[];
}): Promise<any> => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.newsystem.showDownloadMenu') && appVersionCompare('4.1') >= 0) {
            boxx.newapp.newsystem.showDownloadMenu({
                ...params,
                // success: (res: any) => {
                //     resolve(res);
                // },
                // fail: () => {
                //     resolve(0);
                // },
            });
        }
        else {
            console.error('newapp.newsystem.showDownloadMenu 端能力不存在！！！');
            resolve(0);
        }
    });
};

/**
 * @description 调起下载文档面板
 */
const showDocumentMenu = (params: {
    title: string;
    items: object[];
    btnText: string;
    ubc: object;
}): Promise<any> => {
    return new Promise(resolve => {
        if (boxx.canIUse('newsystem.showDocumentMenu') && appVersionCompare('4.8') >= 0) {
            boxx.newsystem.showDocumentMenu({
                ...params,
                success: (res: any) => {
                    resolve(res);
                },
                fail: () => {
                    resolve({});
                },
            });
        }
        else {
            resolve({});
        }
    });
};

/**
 * 下载文档
 *
 * @param params 入参
 */
const downloadDocument = (params: {
    url: string;
    name?: string;
    ubc?: object;
    cancelUbc?: object;
}) => {
    return new Promise(resolve => {
        if (boxx.canIUse('newsystem.downloadDocument')) {
            boxx.newsystem.downloadDocument({
                ...params,
                // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
                success: (res: any) => {
                    resolve(res);
                },
                fail: (res: any) => {
                    resolve(res);
                },
            });
        }
        else {
            resolve({});
            console.error('newsystem.downloadDocument 端能力不存在！！！');
        }
    });
};

const switchModel = (params: {
    modelName: string;
}) => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.newsystem.switchModel')) {
            boxx.newapp.newsystem.switchModel({
                ...params,
                // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
                success: (res: any) => {
                    resolve(res);
                },
                fail: (res: any) => {
                    resolve(res);
                },
            });
        }
        else {
            resolve({});
            console.error('newapp.newsystem.switchModel 端能力不存在！！！');
        }
    });
};

/**
 * 调起音乐播放器
 *
 * @param params 入参
 */
const controlMusicPlayer = (params: MusicPlayerParams) => {
    if (boxx.canIUse('newsystem.musicPlayerControl') && appVersionCompare('3.3') >= 0) {
        boxx.newsystem.musicPlayerControl({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('newsystem.musicPlayerControl success', res);
            // },
            // fail: (res: any) => {
            //     console.log('newsystem.musicPlayerControl success', res);
            // },
        });
    }
    else {
        console.error('newsystem.musicPlayerControl 端能力不存在！！！');
    }
};

interface NotificationPermissionParams {
    title: string;
    content: string;
}

// 调起通知权限弹窗
const invokeNotificationPermissionGuide = (params: NotificationPermissionParams) => {
    return new Promise(resolve => {
        if (boxx.canIUse('newsystem.invokeNotificationPermissionGuide') && appVersionCompare('3.6') >= 0) {
            boxx.newsystem.invokeNotificationPermissionGuide({
                ...params,
                // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
                success: (res: any) => {
                    resolve(res);
                },
                fail: (res: any) => {
                    resolve(res);
                },
            });
        }
        else {
            resolve({});
            console.error('newsystem.invokeNotificationPermissionGuide 端能力不存在！！！');
        }
    });
};
/**
 * 打开多模页
 */
const multiMode = (params: any) => {
    if (boxx.canIUse('newapp.newconversation.multiMode') && appVersionCompare('3.5') >= 0) {
        boxx.newapp.newconversation.multiMode({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('newconversation.multiMode success', res);
            // },
            // fail: (res: any) => {
            //     console.log('newconversation.multiMode fail', res);
            // },
        });
    }
    else {
        console.error('newconversation.multiMode 端能力不存在！！！');
    }
};

/**
 * 播放上传文件动画
 * @description https://es.baidu-int.com/index#/apidetail/2822
 * @returns 返回一个Promise对象，表示异步操作的结果，解析为布尔值，表示动画是否成功播放
 */
const recommendCardGuide = (params: {
    showUrl: string;
    showText: string;
}): Promise<boolean> => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.newconversation.recommendCardGuide') && appVersionCompare('4.3') >= 0) {
            boxx.newapp.newconversation.recommendCardGuide({
                ...params,
                success: () => {
                    resolve(true);
                },
                fail: () => {
                    resolve(false);
                },

            });
        }
        else {
            console.error('newapp.newconversation.recommendCardGuide 端能力不存在！！！');
            resolve(false);
        }
    });
};

/**
 * @description 打开智能体形象上传选择器
 * @param
 *  "appID":     // 相机是否需要人脸抠图
 *  "mode": "camera" | "photo"// 是否直接进拍照
 */
const openAgentUploadPicker = (params: {
    needBeauty?: number;
    mode: string;
    avatarSizeLimit?: number;
    sizeLimit?: number;
    images: Array<{ id: number; url: string }>;
    appId?: number;
}) => {
    if (boxx.canIUse('newapp.newbrowse.openAgentUploadPicker')) {
        boxx.newapp.newbrowse.openAgentUploadPicker({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('newapp.newbrowse.openAgentUploadPicker success', res);
            // },
            // fail: (res: any) => {
            //     console.log('newapp.newbrowse.openAgentUploadPicker fail', res);
            // },
        });
    }
    else {
        console.error('newapp.newbrowse.openAgentUploadPicker 端能力不存在！！！');
    }
};

/**
 * @description 关闭所有新开的页面，回到NA页
 */
const backToNA = () => {
    if (boxx.canIUse('newbrowse.backToNA') && appVersionCompare('3.6') >= 0) {
        boxx.newbrowse.backToNA({
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('newbrowse.backToNA 端能力不存在！！！');
    }
};

/**
 * @description 3.8新增，对话页左上角展示引导气泡tips
 * @param title 提示文字
 * @param deadline 展示时间(单位：秒)
 */
const showGuideTips = (params: {
    title: string;
    deadline: number;
}) => {
    if (boxx.canIUse('newapp.newsystem.showGuideTips') && appVersionCompare('3.8') >= 0) {
        boxx.newapp.newsystem.showGuideTips({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('newapp.newsystem.showGuideTips 端能力不存在！！！');
    }
};

/**
 * @description 3.8新增，新手任务入口状态更新
 * @param isHidden 是否隐藏
 * @param isRetract 是否收起
 */
const talkEncourageUpdate = (params: {
    isHidden?: 0 | 1;
    isRetract?: 0 | 1;
}) => {
    if (boxx.canIUse('newapp.newsystem.talkEncourageUpdate') && appVersionCompare('3.8') >= 0) {
        boxx.newapp.newsystem.talkEncourageUpdate({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('newapp.newsystem.talkEncourageUpdate 端能力不存在！！！');
    }
};

/**
 * @description 打开成长中心升级弹窗(4.0新增)
 * @param level
 * @param desc
 * @param source
 */

const openLevelAlert = (params: {
    level: string;
    desc: string;
    source?: string;
}) => {
    if (boxx.canIUse('newapp.newsystem.levelAlert') && appVersionCompare('4.0') >= 0) {
        boxx.newapp.newsystem.levelAlert({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('newapp.newsystem.levelAlert 端能力不存在！！！');
    }
};

/**
 * @description 3.8新增，吊起主会话页面键盘
 * @param animaiton 出现是否带有动画
 */
const startInput = (params: {
    animaiton?: boolean;
}) => {
    if (boxx.canIUse('newapp.newconversation.startInput') && appVersionCompare('3.8') >= 0) {
        boxx.newapp.newconversation.startInput({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('newapp.newconversation.startInput 端能力不存在！！！');
    }
};

/**
 * @description 4.3新增，吊起地区选择器
 * https://ku.baidu-int.com/knowledge/HFVrC7hq1Q/qCCPuIpbfn/2bDtTCu3yf/Rzd6UauWbp7bhe
 */
const openRegionPicker = (pickRegionCallback: (region: string) => void) => {
    if (boxx.canIUse('newapp.newsystem.openRegionPicker') && appVersionCompare('4.3') >= 0) {
        window.pickRegionCallback = pickRegionCallback;
        boxx.newapp.newsystem.openRegionPicker({
            pickRegionCallback: 'pickRegionCallback',
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: res => {
            //     console.log(res);
            // },
            // fail: res => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('newapp.newsystem.openRegionPicker 端能力不存在！！！');
    }
};

const communityWebviewReady = () => {
    if (boxx.canIUse('newapp.utils.communityPageReady') && appVersionCompare('4.4') >= 0) {
        boxx.newapp.utils.communityPageReady();
    }
};

const saveVideo = (url: string) => {
    if (boxx.canIUse('newapp.newsystem.saveVideo') && appVersionCompare('4.6') >= 0) {
        boxx.newapp.newsystem.saveVideo({
            url,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log(res);
            // },
            // fail: (res: any) => {
            //     console.log(res);
            // },
        });
    }
    else {
        console.error('newapp.newsystem.saveVideo 端能力不存在！！！');
    }
};

const handleTemplateBar = (params: {
    source?: string;
    panel_config?: object;
    downgrade?: {
        query?: string;
        toast?: string;
    };
    id: string;
    content: string;
}) => {
    if (boxx.canIUse('newapp.newbrowse.handleTemplateBar') && appVersionCompare('4.6') >= 0) {
        boxx.newapp.newbrowse.handleTemplateBar({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('newapp.newbrowse.handleTemplateBar', res);
            // },
            // fail: (res: any) => {
            //     console.log('newapp.newbrowse.handleTemplateBar', res);
            // },
        });
    }
    else {
        console.error('newapp.newbrowse.handleTemplateBar 端能力不存在！！！');
    }
};

interface TimePickerRes extends BaseResponse {
    /**
     * 返回的数据
     */
    data: {
        /**
         * 选择的周期，同入参slidOptions
         */
        period: string;
        /**
         * 中文时间 12月18日 20:00
         */
        text: string;
        /**
         * 时间 2024-12-18 20:00:00
         */
        time: string;
    };
}

/**
 * @description 4.6新增，吊起时间选择器
 * https://es.baidu-int.com/index#/apidetail/2855/base
 */
const openTimePickerMenuBar = (params: {
    ext: {
        click_content: string;
    }; // '{"click_content":"submit"}'
    source: string;
    slidOptions?: string[];
    buttonText?: string;
}) => {
    return new Promise<TimePickerRes>(resolve => {
        if (boxx.canIUse('newapp.newbrowse.openTimePickerMenuBar') && appVersionCompare('4.6') >= 0) {
            boxx.newapp.newbrowse.openTimePickerMenuBar({
                ...params,
                success: (res: TimePickerRes) => {
                    resolve(res);
                },
                fail: () => {
                    resolve({
                        status: 1,
                        message: '端能力调用失败！！！',
                        data: {
                            period: '',
                            text: '',
                            time: '',
                        },
                    });
                },
            });
        }
        else {
            resolve({
                status: 0,
                message: 'newapp.newsystem.openTimePickerMenuBar 端能力不存在！！！',
                data: {
                    period: '',
                    text: '',
                    time: '',
                },
            });
        }
    });
};

/**
 * @description 通知页 获取顶bar icon信息
 */
const getModelsInfo = (): Promise<ModelsInfo> => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.chat.getModelsInfo') && appVersionCompare('4.12') >= 0) {
            boxx.newapp.chat.getModelsInfo({
                success: (res: ModelsInfoRes) => {
                    resolve(res.data || {});
                },
                fail: () => {
                    resolve({});
                },
            });
        }
        else {
            console.error('newapp.chat.getModelsInfo 端能力不存在！！！');
            resolve({});
        }
    });
};

const openMultiModeTakePhoto = (params: {
    needSave?: string;
    selectedIndex: string;
    source?: string;
}) => {
    if (boxx.canIUse('newapp.newsystem.openMultiModeTakePhoto') && appVersionCompare('4.13') >= 0) {
        boxx.newapp.newsystem.openMultiModeTakePhoto({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('openMultiModeTakePhoto success ====', res);
            // },
            // fail: (res: any) => {
            //     console.log('openMultiModeTakePhoto fail ====', res);
            // },
        });
    }
    else {
        console.error('newapp.newsystem.openMultiModeTakePhoto 端能力不存在！！！');
    }
};

// 打开社区发布页
const openPublisher = (
    params: Partial<{
        topicId: number;
        title: string;
        source: string;
        useNewPanel: string;
    }>
) => {
    if (boxx.canIUse('newapp.newbrowse.openPublisher') && appVersionCompare('4.19') >= 0) {
        boxx.newapp.newbrowse.openPublisher({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('openPublisher success ====', res);
            // },
            // fail: (res: any) => {
            //     console.log('openPublisher fail ====', res);
            // },
        });
    }
    else {
        console.error('newapp.newsystem.openPublisher 端能力不存在！！！');
    }
};

// 通知 NA 预加载创作面板
const prePublisherData = (
    params: {
        source: string;
    }
) => {
    if (boxx.canIUse('newapp.newbrowse.prePublisherData')) {
        boxx.newapp.newbrowse.prePublisherData({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('prePublisherData success ====', res);
            // },
            // fail: (res: any) => {
            //     console.log('prePublisherData fail ====', res);
            // },
        });
    }
    else {
        console.error('newapp.newsystem.prePublisherData 端能力不存在！！！');
    }
};

const openMagicRetouch = (params: {
    prompt?: string;
    templateId?: string;
    source?: string;
}) => {
    // 高于4.22的ios或所有安卓直接调修图端能力，安卓端上自己会判断出升级弹窗
    if (boxx.canIUse('magicRetouch.edit') && (appVersionCompare('4.22') >= 0 || isAndroid)) {
        boxx.magicRetouch.edit({
            ...params,
        });
    }
    else {
        showNaToast('功能暂未开放，敬请期待');
        console.error('magicRetouch.edit 端能力不存在！！！');
    }
};

/**
 * newapp控制删除框上引导
 */

const deleteGuide = (params: {
    id?: string;
}) => {
    if (boxx.canIUse('newapp.newconversation.deleteGuide') && appVersionCompare('4.25') >= 0) {
        boxx.newapp.newconversation.deleteGuide({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('newapp.newconversation.deleteGuide success', res);
            // },
            // fail: (res: any) => {
            //     console.log('newapp.newconversation.deleteGuide success', res);
            // },
        });
    }
    else {
        // console.error('newapp.newconversation.deleteGuide 端能力不存在！！！');
    }
};

const showInputWithSug = (params: {
    id: string;
}) => {
    if (boxx.canIUse('newapp.newconversation.showInputWithSug') && appVersionCompare('4.25') >= 0) {
        boxx.newapp.newconversation.showInputWithSug({
            ...params,
            // debug时可以解开注释，success为调用端能力成功回调，fail为调用端能力失败回调
            // success: (res: any) => {
            //     console.log('newapp.newconversation.showInputWithSug success', res);
            // },
            // fail: (res: any) => {
            //     console.log('newapp.newconversation.showInputWithSug success', res);
            // },
        });
    }
    else {
        // console.error('newapp.newconversation.showInputWithSug 端能力不存在！！！');
    }
};

const showTaskView = (params: {
    positionY?: number;
    positionX?: number;
    task: string[];
}) => {
    if (boxx.canIUse('newbrowse.showTaskView')) {
        boxx.newbrowse.showTaskView({
            ...params,
        });
    }
    else {
        console.error('newbrowse.showTaskView 端能力不存在！！！');
    }
};

const taskAddAction = (params: {
    distinctId?: string;
    actTaskId?: string;
    taskId?: string;
}) => {
    if (boxx.canIUse('newbrowse.taskAddAction')) {
        boxx.newbrowse.taskAddAction({
            ...params,
        });
    }
    else {
        console.error('newbrowse.taskAddAction 端能力不存在！！！');
    }
};

const saveMultiImage = (params: {
    downloadImages: string[];
}): Promise<void> => {
    return new Promise((resolve, reject) => {
        if (boxx.canIUse('newsystem.saveMultiImage')) {
            boxx.newsystem.saveMultiImage({
                ...params,
                success: () => {
                    resolve();
                },
                fail: () => {
                    reject();
                },
            });
        }
        else {
            reject();
        }
    });
};

const imgRetouchComplete = (params: {
    imageUrl: string;
    imageId: number;
    nid: string;
}): Promise<void> => {
    return new Promise((resolve, reject) => {
        if (boxx.canIUse('comics.imgRetouchComplete')) {
            boxx.comics.imgRetouchComplete({
                ...params,
                success: () => {
                    resolve();
                },
                fail: () => {
                    reject();
                },
            });
        }
        else {
            reject();
        }
    });
};

const comicWorkStatusChange = (params: {
    pubStatus: 0 | 1;
    nid: string;
    communityPostNid?: string;
}) => {
    if (boxx.canIUse('newbrowse.comicWorkStatusChange') && appVersionCompare('5.2') >= 0) {
        boxx.newbrowse.comicWorkStatusChange({
            ...params,
        });
    }
    else {
        console.error('newbrowse.comicWorkStatusChange 端能力不存在！！！');
    }
};

/**
 * @description 获取底部导航栏高度
 */
export const getNavBarHeight = (): Promise<number> => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.screen.getStatusBarHeight')) {
            boxx.newapp.screen.getStatusBarHeight({
                success: (res: { data: { navBarHeight: number } }) => {
                    resolve(res.data?.navBarHeight || 0);
                },
                fail: () => {
                    resolve(0);
                },
            });
        }
        else {
            // console.error('newapp.screen.getStatusBarHeight 端能力不存在！！！');
            resolve(0);
        }
    });
};

interface CloseAndOpenDetailParams extends OpenDetailParams {
    noAnimation?: boolean;
}

/**
 * @description 关闭当前页面并打开一个新的detail页面
 * @param params OpenDetailParams + noAnimation参数（默认为true，控制是否使用关闭页面、开页面动画）
 * @returns void
 */
const closeAndOpenDetail = (params: CloseAndOpenDetailParams) => {
    const {noAnimation = true, ...openDetailParams} = params;
    // 如果不关闭动画，直接调用close然后open即可
    if (!noAnimation) {
        closeBrowse();
        openDetail(openDetailParams);
        return;
    }
    // iOS端，forbiddenAnimation = 1时，存在close后立刻释放页面内存的情况，导致后续页面跳转代码无法执行
    // 所以此处需要以下特殊处理
    !isIOS && closeBrowse({
        forbiddenAnimation: noAnimation ? 1 : 0,
    });
    const needSingleTask = isIOS && noAnimation;
    const schema = `baiduwanhua://newbrowse/detail?params=${encodeURIComponent(
        JSON.stringify({
            ...openDetailParams,
            openAnimation: noAnimation ? (isAndroid ? false : 0) : (isAndroid ? true : 1),
        })
    )}${needSingleTask ? '&launchMode=singleTask' : ''}`;
    invokeSchema(schema);
};

/**
 * @description 添加widget
 * https://es.baidu-int.com/index#/apidetail/3160/invoke
 */
const addDeskWidget = (params = {}): Promise<string> => {
    return new Promise(resolve => {
        if (boxx.canIUse('newapp.newsystem.addDeskWidget') && appVersionCompare('5.4') >= 0) {
            boxx.newapp.newsystem.addDeskWidget({
                ...params,
                success: (res: { data: { isAdd: string } }) => {
                    resolve(res.data?.isAdd || '0');
                },
                fail: (err: any) => {
                    console.error(err);
                    resolve('0');
                },
            });
        }
        else {
            console.error('newapp.newsystem.addDeskWidget 端能力不存在！！！');
            resolve('0');
        }
    });
};

/**
 * @description 触发删除确认弹窗
 */
const showBottomMenu = (params?: any) => {
    if (boxx.canIUse('newbrowse.showBottomMenu')) {
        boxx.newbrowse.showBottomMenu({
            ...params,
        });
    }
    else {
        console.error('newapp.newbrowse.showBottomMenu 端能力不存在！！！');
    }
};

export {
    addDeskWidget,
    appUpgrade,
    audioIn,
    audioStatusChange,
    authorizeLocation,
    authorizeLocationDisplayed,
    backToNA,
    cancelMonthlyPay,
    checkModelStatusFromNA,
    closeAndOpenDetail,
    closeBrowse,
    comicWorkStatusChange,
    communityWebviewReady,
    controlMusicPlayer,
    createGreetingsVideo,
    createShare,
    createVideo,
    deleteGuide,
    deleteImage,
    deleteMsg,
    detailContextMenu,
    detailPostSelectionInfo,
    deviceVibrate,
    downloadDocument,
    feSendToNa,
    getAuthentication,
    getCommonParams,
    getFontSize,
    getFrameData,
    getGlobalSettings,
    getHostTag,
    getIconInfo,
    getInteractionData,
    getModelsInfo,
    getNetworkStatus,
    getNotificationStatus,
    getPreviewHeight,
    getQequestChannelList,
    getSettinInfo,
    getStartPay,
    getStatusBarHeight,
    getUserInfoFromNA,
    getVolume,
    getWechatInstalled,
    handleTemplateBar,
    hideBackBtn,
    hideLoading,
    imgRetouchComplete,
    invokeImgPreview,
    invokeMenuPannel,
    invokeMiMarket,
    invokeNotificationPermissionGuide,
    invokePaymentPanel,
    invokeQuestionEdit,
    invokeSearchPannel,
    invokeSettings,
    invokeSystemPushSettings,
    isMiMobile,
    isSupportVirtualMan,
    keepDeviceAwake,
    multiMode,
    openAgentUploadPicker,
    openAIFigureUpload,
    openCharacterSettings,
    openChat,
    openCommunityDetail,
    openDetail,
    openFullScreenVoiceChat,
    openImagePickerAndUpload,
    openImageToVideoMainPage,
    openInteraction,
    openLevelAlert,
    openLoading,
    openMagicRetouch,
    openModelUnlockPanel,
    openMultiModeTakePhoto,
    openNAFlowVideo,
    openNAVideo,
    openPrompt,
    openPublisher,
    openRegionPicker,
    openShareDialog,
    openTimePickerMenuBar,
    openUserProfile,
    pcClientCanIUse,
    playOfflineTTS,
    playOnlineTTS,
    postSelectionInfo,
    prefetchNAVideo,
    prePublisherData,
    recommendCardGuide,
    riskControl,
    routeMainChat,
    saveImageToAlbum,
    saveMsg,
    saveMultiImage,
    saveVideo,
    seekTab,
    sendChartImageData,
    sendCommunityInfo,
    sendDayCardCountDown,
    sendMessageToNA,
    sendPageTrack,
    sendScreenshotResult,
    sendSound,
    setAllMsgRead,
    setGlobalSettings,
    shareImage,
    showBottomMenu,
    showDocumentMenu,
    showDownloadMenu,
    showFeedback,
    showGuideTips,
    showInputWithSug,
    showMapPannel,
    showMenu,
    showNaToast,
    showReload,
    showTaskView,
    startInput,
    // stopTtsAndNotifyNa,
    switchModel,
    talkEncourageUpdate,
    taskAddAction,
    uploadCompleteAndSendToChat,
    uploadImage,
    useWebStorage,
    writeClipboard,
};
